import React, { useEffect, useRef } from 'react';

import {
  DisplayPlacementId,
  PlacementStatus
} from 'node_modules/@schibsted-nmp/advertising-shared/src/types/api/index.js';

import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { useInScreen } from '@client/core/hooks/index.js';
import {
  BapSearchAd,
  AdImage
} from '@schibsted-nmp/search-frontend-components/react';

import {
  trackMartailerClick,
  trackMartailerImpression,
  type MartailerProduct
} from '../utils/MartailerApi.js';

export type SimpleBapSearchAdProps = {
  layout?: 'grid' | 'list';
  onClick?: () => void;
  products: Array<MartailerProduct>;
};

export type LabelRenderProps = {
  className?: string;
  classes: {
    badge: string;
    info: string;
    warning: string;
    success: string;
    [key: string]: string;
  };
};

export function SponsoredProductAd({
  layout = 'grid',
  onClick,
  products
}: SimpleBapSearchAdProps) {
  const product = products[0];

  const adRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);

  // Track viewability
  const { hasIntersected } = useInScreen({
    ref: adRef,
    threshold: 0.5,
    rootMargin: '0px'
  });

  useEffect(() => {
    if (hasIntersected && !hasTrackedView.current && product) {
      console.log('Ad viewed');
      hasTrackedView.current = true;
      updateMetricByKey(
        DisplayPlacementId.AdvtSplMiddle1,
        PlacementStatus.AdViewed
      );

      void trackMartailerImpression({
        adId: product.adId,
        itemId: product.itemId,
        placement: 'home',
        visitorId: 'test-user',
        category: product.category,
        store: 'finn_recommerce'
      });
    }
  }, [hasIntersected, product]);

  const handleClick = () => {
    console.log('Ad clicked');

    if (product) {
      void trackMartailerClick({
        adId: product.adId,
        itemId: product.itemId,
        placement: 'home',
        visitorId: 'test-user',
        category: product.category,
        store: 'finn_recommerce'
      });
    }

    onClick?.();
  };

  if (!product) return null;

  const bapAd = convertToBapFormat(product);

  const IMAGE_SIZES =
    '(min-width: 1300px) 306px, (min-width: 1010px) 316px, (min-width: 768px) 33vw, (min-width: 480px) 50vw, 100vw';

  return (
    <div ref={adRef}>
      <BapSearchAd
        ad={bapAd}
        currentTime={Date.now()}
        id={bapAd.ad_id}
        imageSizes={IMAGE_SIZES}
        layout={layout}
        onClick={handleClick}
        url={bapAd.canonical_url}
        className="sponsored-product-ad"
        image={
          layout === 'grid'
            ? () => (
                <div style={{ position: 'relative' }}>
                  {/* Header positioned absolutely with white background */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      backgroundColor: 'white',
                      zIndex: 1
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <span
                      style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#1B1B1F'
                      }}
                    >
                      Reklame
                    </span>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      style={{
                        color: '#0063FB',
                        textDecoration: 'none',
                        fontSize: '12px',
                        fontWeight: 300
                      }}
                    >
                      Ad info
                    </a>
                  </div>
                  {/* Image at normal size */}
                  <AdImage
                    aspectClass="aspect-3/4"
                    height={bapAd.image.height}
                    width={bapAd.image.width}
                    url={bapAd.image.url}
                    crop={true}
                    sizes={IMAGE_SIZES}
                  />
                </div>
              )
            : undefined
        }
      />
    </div>
  );
}

function convertToBapFormat(product: MartailerProduct) {
  return {
    ad_id: product.adId,
    canonical_url: product.link,
    distance: null,
    extras: [],
    flags: [],
    heading: product.title,
    image: {
      aspect_ratio: 1.3333333333333333,
      height: 400,
      url: product.imageLink,
      width: 533
    },
    labels: [],
    location: '',
    main_search_key: 'SEARCH_ID_BAP_COMMON',
    organisation_name: product.organisationName,
    price: {
      currency_code: 'NOK',
      amount: product.price,
      price_unit: 'kr'
    },
    timestamp: 1234567890,
    trade_type: 1
  };
}
